import * as React from 'react';
import { ICenterAddress, ICenterBase, IContactEntityTypePositions, IListItem, ILogisticsProvider } from '@nkr/models/index';
import { ContactEntityType } from '@nkr/enums/index';
import { ControlLabel, Form, FormControl, FormGroup } from 'react-bootstrap';
import { IOverrideAddress } from './override-address';
import { IValidation } from './validation';
import Roles from './roles';

interface IProps {
    isCenter: boolean,
    organType: number,
    entityType: ContactEntityType,
    entityTypeId: number,
    positions: number[],
    title: string,
    prefix: string,
    firstName: string,
    middleName: string,
    lastName: string,
    suffix: string,
    email: string,
    secondaryEmail: string,
    primaryAddress: number,
    primarySuite: string,
    primaryAddressOverride: IOverrideAddress,
    secondaryAddress: number,
    secondarySuite: string,
    secondaryAddressOverride: string,
    validation: IValidation,
    centerList: ICenterBase[],
    courierList: ILogisticsProvider[],
    contactEntityTypes: IListItem[],
    positionList: IContactEntityTypePositions[],
    centerAddressList: ICenterAddress[],
    onNumberValueChange: (property: string, value: number) => void,
    onAddRoles: (list: number[]) => void,
    onRemoveRoles: (list: number[]) => void,
    onTextChange: (property: string, value: string) => void
}

const ContactInfoContent = (props: IProps) => {
    const handleNumberValueChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        props.onNumberValueChange(name, parseInt(value));
    }

    const handleAddRoles = (list: number[]) => {
        props.onAddRoles(list);
    }

    const handleRemoveRoles = (list: number[]) => {
        props.onRemoveRoles(list);
    }

    const handleTextChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        props.onTextChange(name, value);
    }

    return (
        <Form>
            {props.isCenter == false && (
                <FormGroup style={{ marginRight: 30 }} validationState={!props.validation.entityType ? 'error' : null}>
                    <ControlLabel>Organ Type</ControlLabel>
                    <FormControl
                        componentClass='select'
                        name='organType'
                        value={props.organType}
                        onChange={handleNumberValueChange}
                    >
                        <option value={-1}>- BOTH -</option>
                        <option value={0}>Kidney</option>
                        <option value={1}>Liver</option>
                    </FormControl>
                </FormGroup>
            )}

            <div style={{ display: 'flex' }}>
                {props.isCenter == false && (
                    <FormGroup style={{ marginRight: 30 }} validationState={!props.validation.entityType ? 'error' : null}>
                        <ControlLabel>Contact Type</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='entityType'
                            value={props.entityType}
                            onChange={handleNumberValueChange}
                        >
                            <option value={0}>- Select Contact Type -</option>
                            {props.contactEntityTypes.map((cet, index) =>
                                <option key={index} value={cet.id}>{cet.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                )}

                {props.isCenter == false && props.entityType == ContactEntityType.Center && (
                    <FormGroup>
                        <ControlLabel>Center</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='center'
                            value={props.entityTypeId}
                            onChange={handleNumberValueChange}
                        >
                            <option value={0}>- Select Center -</option>
                            {props.centerList.map((center, index) =>
                                <option key={index} value={center.id}>{center.alias}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                )}

                {props.entityType == ContactEntityType.SwapCourier && (
                    <FormGroup>
                        <ControlLabel>Courier</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='courier'
                            value={props.entityTypeId}
                            onChange={handleNumberValueChange}
                        >
                            <option value={0}>- Select Courier -</option>
                            {props.courierList.map((courier, index) =>
                                <option key={index} value={courier.id}>{courier.description}</option>
                            )}
                        </FormControl>
                    </FormGroup>
                )}

            </div>

            {(props.entityType == ContactEntityType.Center || props.entityType == ContactEntityType.Opo || props.entityType == ContactEntityType.Nkr) && (
                <FormGroup>
                    <ControlLabel>Roles</ControlLabel>

                    <Roles
                        entityType={props.entityType}
                        selectedRoles={props.positions}
                        otherText=''
                        positionList={props.positionList}
                        valid={props.validation.positions}
                        onAdd={handleAddRoles}
                        onRemove={handleRemoveRoles}
                        onOtherTextChange={() => null}
                    />
                </FormGroup>
            )}

            <FormGroup>
                <ControlLabel>Title</ControlLabel>
                <FormControl
                    name='title'
                    value={props.title}
                    onChange={handleTextChange}
                />
            </FormGroup>

            <FormGroup>
                <ControlLabel>Name Prefix</ControlLabel>
                <FormControl
                    name='prefix'
                    value={props.prefix}
                    maxLength={8}
                    onChange={handleTextChange}
                />
            </FormGroup>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                <FormGroup style={{ marginRight: 5 }} validationState={!props.validation.firstName ? 'error' : null}>
                    <ControlLabel>First Name</ControlLabel>
                    <FormControl
                        name='firstName'
                        value={props.firstName}
                        onChange={handleTextChange}
                    />
                </FormGroup>

                <FormGroup style={{ marginRight: 5 }}>
                    <ControlLabel>Middle Name</ControlLabel>
                    <FormControl
                        name='middleName'
                        value={props.middleName}
                        onChange={handleTextChange}
                    />
                </FormGroup>

                <FormGroup validationState={!props.validation.lastName ? 'error' : null}>
                    <ControlLabel>Last Name</ControlLabel>
                    <FormControl
                        name='lastName'
                        value={props.lastName}
                        onChange={handleTextChange}
                    />
                </FormGroup>
            </div>

            <FormGroup>
                <ControlLabel>Name Suffix</ControlLabel>
                <FormControl
                    name='suffix'
                    value={props.suffix}
                    onChange={handleTextChange}
                />
            </FormGroup>

            <FormGroup validationState={!props.validation.email ? 'error' : null}>
                <ControlLabel>Email Address</ControlLabel>
                <FormControl
                    name='email'
                    value={props.email}
                    onChange={handleTextChange}
                />
            </FormGroup>

            <FormGroup>
                <ControlLabel>Secondary Email Address</ControlLabel>
                <FormControl
                    name='secondaryEmail'
                    value={props.secondaryEmail}
                    onChange={handleTextChange}
                />
            </FormGroup>

            {props.entityType == ContactEntityType.Center && (
                <>
                    <FormGroup validationState={!props.validation.primaryAddress ? 'error' : null}>
                        <ControlLabel>Primary Address</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='primaryAddress'
                            value={props.primaryAddress}
                            onChange={handleNumberValueChange}
                        >
                            <option value={0}>- Select Address -</option>
                            {props.centerAddressList.map((ca, index) =>
                                <option key={index} value={ca.id}>{ca.typeName}: {ca.addressLine1 + (ca.addressLine2 ? ', ' + ca.addressLine2 : '') + (ca.addressLine3 ? ', ' + ca.addressLine3 : '')} {ca.city}, {ca.stateAbbreviation} {ca.postalCode}</option>
                            )}
                        </FormControl>
                    </FormGroup>

                    <FormGroup>
                        <ControlLabel>Primary Mail Stop/Suite</ControlLabel>
                        <FormControl
                            name='primarySuite'
                            value={props.primarySuite}
                            onChange={handleTextChange}
                        />
                    </FormGroup>
                </>
            )}

            {props.entityType != ContactEntityType.Center && (
                <>
                    <ControlLabel>Primary Address</ControlLabel>

                    <FormGroup validationState={!props.validation.primaryAddressOverride.street ? 'error' : null}>
                        <div>Street</div>
                        <FormControl
                            name='primaryAddressOverride.street'
                            value={props.primaryAddressOverride.street}
                            onChange={handleTextChange}
                        />
                    </FormGroup>

                    <div style={{ display: 'flex' }}>
                        <FormGroup style={{ width: '60%', marginRight: 5 }} validationState={!props.validation.primaryAddressOverride.city ? 'error' : null}>
                            <div>City</div>
                            <FormControl
                                name='primaryAddressOverride.city'
                                value={props.primaryAddressOverride.city}
                                onChange={handleTextChange}
                            />
                        </FormGroup>

                        <FormGroup style={{ marginRight: 5 }} validationState={!props.validation.primaryAddressOverride.state ? 'error' : null}>
                            <div>State</div>
                            <FormControl
                                name='primaryAddressOverride.state'
                                value={props.primaryAddressOverride.state}
                                onChange={handleTextChange}
                            />
                        </FormGroup>

                        <FormGroup style={{ width: '20%' }} validationState={!props.validation.primaryAddressOverride.postalCode ? 'error' : null}>
                            <div>Postal Code</div>
                            <FormControl
                                name='primaryAddressOverride.postalCode'
                                value={props.primaryAddressOverride.postalCode}
                                onChange={handleTextChange}
                            />
                        </FormGroup>
                    </div>
                </>
            )}

            <FormGroup>
                <ControlLabel>Secondary Address</ControlLabel>
                <FormControl
                    componentClass='select'
                    name='secondaryAddress'
                    value={props.secondaryAddress}
                    onChange={handleNumberValueChange}
                >
                    <option value={0}>- No Secondary Address -</option>
                    <option value={-1}>- Custom Secondary Address -</option>
                    {props.centerAddressList.map((ca, index) =>
                        <option key={index} value={ca.id}>{ca.typeName}: {ca.addressLine1 + (ca.addressLine2 ? ', ' + ca.addressLine2 : '') + (ca.addressLine3 ? ', ' + ca.addressLine3 : '')} {ca.city}, {ca.stateAbbreviation} {ca.postalCode}</option>
                    )}
                </FormControl>
            </FormGroup>

            {props.secondaryAddress > 0 && (
                <FormGroup>
                    <ControlLabel>Secondary Mail Stop/Suite</ControlLabel>
                    <FormControl
                        name='secondarySuite'
                        value={props.secondarySuite}
                        onChange={handleTextChange}
                    />
                </FormGroup>
            )}

            {props.secondaryAddress == -1 && (
                <FormGroup>
                    <ControlLabel>Custom Secondary Address</ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        name='secondaryAddressOverride'
                        value={props.secondaryAddressOverride}
                        onChange={handleTextChange}
                    />
                </FormGroup>
            )}
        </Form>
    );
}

export default ContactInfoContent;